/**
 * R3 — Severity × coverage quadrant. The cross-cutting "what's under the
 * radar?" view, in answer to the question a per-outbreak badge can't:
 *   "What across the whole board is dangerously under-covered?"
 *
 * X axis = coverage volume (silent / low / medium / high).
 * Y axis = threat severity (CRITICAL at top → WATCH at bottom).
 * Top-left zone = the operational priority — high-severity + low-coverage.
 * Hover any point for its detail line in the readout strip.
 *
 * Per CD a11y note: points carry SHAPE (via tooltip) + POSITION, not colour
 * alone. The dashed-ring overlay on under-reported outbreaks is the colour-
 * independent flag a colour-blind operator can still read.
 */

import { useState, useMemo } from 'react';

const TIER_WORD: Record<number, string> = { 1: 'CRITICAL', 2: 'HIGH', 3: 'ELEVATED', 4: 'WATCH', 0: 'ROUTINE' };
const TIER_COLOR: Record<number, string> = {
  1: 'var(--bm-threat-1)', 2: 'var(--bm-threat-2)',
  3: 'var(--bm-threat-3)', 4: 'var(--bm-threat-4)', 0: 'var(--bm-threat-0)',
};
const COV_WORD = ['Silent', 'Low', 'Medium', 'High'];

interface QuadrantPoint {
  outbreakId: string;
  common: string;
  admin: string;
  threatTier: 0 | 1 | 2 | 3 | 4;
  coverageLevel: 0 | 1 | 2 | 3;
  asymmetryKind: 'under' | 'high' | null;
}

export interface CoverageQuadrantProps {
  points: readonly QuadrantPoint[];
  width?: number;
  height?: number;
}

export function CoverageQuadrant({ points, width = 640, height = 420 }: CoverageQuadrantProps) {
  const [hi, setHi] = useState<string | null>(null);

  const padL = 70;
  const padR = 24;
  const padT = 34;
  const padB = 60;
  const pw = width - padL - padR;
  const ph = height - padT - padB;

  // CRITICAL tier (=1) should be at top — invert.
  const sevRank: Record<number, number> = { 1: 4, 2: 3, 3: 2, 4: 1, 0: 0 };
  const xOf = (lvl: number) => padL + (lvl / 3) * pw;
  const yOf = (tier: number) => padT + ph - (sevRank[tier]! / 4) * ph;

  const positioned = useMemo(() => points
    .filter(p => p.threatTier !== 0)        // routine tier sits off-axis
    .map(p => ({ p, x: xOf(p.coverageLevel), y: yOf(p.threatTier) })),
    [points, padL, padT, pw, ph],
  );

  const hiPoint = hi ? points.find(p => p.outbreakId === hi) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="Severity by coverage scatter — under-reported outbreaks (top-left zone) highlight first"
      >
        {/* Under-reported zone backdrop (top-left, high severity + low coverage) */}
        <rect
          x={padL} y={padT}
          width={pw * 0.45} height={ph * 0.5}
          fill="var(--bm-cov-under)" opacity="0.08"
        />
        <text
          x={padL + 8} y={padT + 18}
          fontFamily="var(--bm-font-mono)" fontSize="11" fill="var(--bm-cov-under)" opacity="0.9"
        >◍ UNDER-REPORTED</text>
        <text
          x={padL + 8} y={padT + 32}
          fontFamily="var(--bm-font-mono)" fontSize="9.5" fill="var(--bm-text-subtle)"
        >high severity · low coverage</text>

        {/* axes */}
        <line x1={padL} x2={padL} y1={padT} y2={padT + ph} stroke="var(--bm-border-strong)" />
        <line x1={padL} x2={padL + pw} y1={padT + ph} y2={padT + ph} stroke="var(--bm-border-strong)" />

        {/* y-axis tier labels */}
        {([[4, 'CRITICAL', 1], [3, 'HIGH', 2], [2, 'ELEVATED', 3], [1, 'WATCH', 4]] as const).map(([rank, label, tier]) => (
          <text
            key={tier}
            x={padL - 10} y={padT + ph - (rank / 4) * ph + 4}
            textAnchor="end"
            fontFamily="var(--bm-font-mono)" fontSize="10" fill={TIER_COLOR[tier]}
          >{label}</text>
        ))}

        {/* x-axis coverage labels */}
        {[0, 1, 2, 3].map(l => (
          <text
            key={l}
            x={xOf(l)} y={padT + ph + 18}
            textAnchor="middle"
            fontFamily="var(--bm-font-mono)" fontSize="10" fill="var(--bm-text-subtle)"
          >{COV_WORD[l]}</text>
        ))}

        <text
          x={padL + pw / 2} y={height - 8} textAnchor="middle"
          fontFamily="var(--bm-font-mono)" fontSize="10.5" fill="var(--bm-text-muted)"
        >media coverage →</text>
        <text
          x={18} y={padT + ph / 2}
          transform={`rotate(-90 18 ${padT + ph / 2})`}
          textAnchor="middle"
          fontFamily="var(--bm-font-mono)" fontSize="10.5" fill="var(--bm-text-muted)"
        >threat severity →</text>

        {/* points */}
        {positioned.map(({ p, x, y }) => {
          const isHi = hi === p.outbreakId;
          return (
            <g
              key={p.outbreakId}
              onMouseEnter={() => setHi(p.outbreakId)}
              onMouseLeave={() => setHi(null)}
              style={{ cursor: 'pointer' }}
            >
              {p.asymmetryKind === 'under' && (
                <circle
                  cx={x} cy={y} r="13"
                  fill="none" stroke="var(--bm-cov-under)"
                  strokeWidth="1.5" strokeDasharray="2 2" opacity="0.85"
                />
              )}
              <circle
                cx={x} cy={y} r={isHi ? 8 : 6}
                fill={TIER_COLOR[p.threatTier]}
                fillOpacity={isHi ? 1 : 0.85}
                stroke="var(--bm-bg)" strokeWidth="1.5"
              />
              {isHi && (
                <text
                  x={x} y={y - 16}
                  textAnchor="middle"
                  fontFamily="var(--bm-font-sans)" fontWeight="600" fontSize="11.5"
                  fill="var(--bm-text)"
                >{p.common}</text>
              )}
            </g>
          );
        })}
      </svg>

      <div style={{
        padding: '8px 12px',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
        fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-muted)',
        minHeight: 22,
      }}>
        {hiPoint ? (
          <span>
            <b style={{ color: TIER_COLOR[hiPoint.threatTier] }}>{hiPoint.common}</b>
            <span style={{ color: 'var(--bm-text-subtle)' }}> · {hiPoint.admin} · {TIER_WORD[hiPoint.threatTier]} · {COV_WORD[hiPoint.coverageLevel]} coverage</span>
            {hiPoint.asymmetryKind && (
              <span style={{ color: hiPoint.asymmetryKind === 'under' ? 'var(--bm-cov-under)' : 'var(--bm-cov-high)' }}>
                {' · '}{hiPoint.asymmetryKind === 'under' ? 'under-reported' : 'high-profile'}
              </span>
            )}
          </span>
        ) : (
          <span style={{ color: 'var(--bm-text-subtle)' }}>
            hover a point · dashed ring = under-reported (high severity, low coverage)
          </span>
        )}
      </div>
    </div>
  );
}
